import { State as DataState } from "@progress/kendo-data-query";

const latestStoreVersion = 1.4;

const configItemKeyPaths = {
  storeVersion: "viewConfiguration.BrokerDealsDashboard.storeVersion",
  columns: "viewConfiguration.BrokerDealsDashboard.columns",
  dataState: "viewConfiguration.BrokerDealsDashboard.dataState"
};

export interface ViewConfiguration {
  columns?: string[];
  dataState?: DataState;
}

export const saveViewConfiguration = (config: ViewConfiguration) => {
  const { columns, dataState } = config;
  
  if (columns) {
    const columnsString = columns.join(",");
    window.localStorage.setItem(configItemKeyPaths.columns, columnsString);
  }
  
  if (dataState) {
    const serialized = JSON.stringify(dataState);
    window.localStorage.setItem(configItemKeyPaths.dataState, serialized);
  }
};

// "Migrating" the store clears saved values in case they are incompatible with
// new view code. This will reset a user's preferred view configuration to the
// view's default.
const migrateStoreIfNeeded = () => {
  const storeVersionValue = window.localStorage.getItem(configItemKeyPaths.storeVersion) ?? "";
  const storeVersion = parseFloat(storeVersionValue);
  if (!storeVersion || storeVersion < latestStoreVersion) {
    // Don't use clear() because there may be content in localStorage outside
    // the domain of view configuration persistence that we don't want to clear.
    Object.values(configItemKeyPaths).forEach(keyPath => window.localStorage.removeItem(keyPath));
  }
  window.localStorage.setItem(configItemKeyPaths.storeVersion, latestStoreVersion.toString());
};

export const getViewConfiguration = () => {
  migrateStoreIfNeeded();
  
  const config: ViewConfiguration = {};
  
  const columnsString = window.localStorage.getItem(configItemKeyPaths.columns);
  const columns: string[] = columnsString ? columnsString.split(",") : [];
  config.columns = columns;
  
  const serializedDataState = window.localStorage.getItem(configItemKeyPaths.dataState);
  if (serializedDataState) {
    const dataState = JSON.parse(serializedDataState, (_key, value) => {
      if (typeof value === "string") {
        const parsedDate = new Date(value);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      }
      return value;
    });
    if (dataState) {
      config.dataState = dataState;
    }
  }
  
  return config;
};
